import React, { useState, useEffect } from 'react';
import Label from '@sportnet/ui/Label/Label';
import Loader from '@sportnet/ui/Loader';
import Api from '../../Api';
import { __ } from '../../utilities';
import getApiError from '../../utilities/getApiError';

const RolesSelector = ({ appSpace, selectedRoles, onChange, disabled }) => {
  const [appSpaceRoles, setAppSpaceRoles] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedPrimaryRoles, setSelectedPrimaryRoles] = useState({});
  // Structure: { primaryAppId: { secondaryAppId: roleId } }
  const [selectedSecondaryRoles, setSelectedSecondaryRoles] = useState({});

  useEffect(() => {
    const fetchAppSpaceRoles = async () => {
      setIsFetching(true);
      try {
        const res = await Api.crmAppSpaceRoles(appSpace);
        setAppSpaceRoles(res.appspaces || []);
      } catch (e) {
        const err = getApiError(e);
        alert(
          `Chyba získania rolí: ${err.details?.statusCode} ${err.details?.name} ${err.message}`,
        );
      } finally {
        setIsFetching(false);
      }
    };

    if (appSpace) {
      fetchAppSpaceRoles();
    }
  }, [appSpace]);

  useEffect(() => {
    // Initialize selected roles from props
    if (selectedRoles && selectedRoles.length > 0) {
      const primaryRoles = {};
      const secondaryRoles = {};

      selectedRoles.forEach(role => {
        const appId = role.app_id;
        const roleId = role.role;

        // This is a primary app
        primaryRoles[appId] = roleId;

        // Parse child apps roles if they exist
        if (role.child_apps_role && Array.isArray(role.child_apps_role)) {
          secondaryRoles[appId] = {};
          role.child_apps_role.forEach(childRole => {
            secondaryRoles[appId][childRole.app_id] = childRole.role;
          });
        }
      });

      setSelectedPrimaryRoles(primaryRoles);
      setSelectedSecondaryRoles(secondaryRoles);
    }
  }, [selectedRoles, appSpaceRoles]);

  const handlePrimaryRoleChange = (appId, roleId) => {
    let newSecondaryRoles = { ...selectedSecondaryRoles };
    const newPrimaryRoles = {
      ...selectedPrimaryRoles,
      [appId]: roleId,
    };

    // If role is removed (empty), remove secondary roles for this app
    if (!roleId) {
      delete newPrimaryRoles[appId];
      delete newSecondaryRoles[appId];
    }

    setSelectedPrimaryRoles(newPrimaryRoles);
    setSelectedSecondaryRoles(newSecondaryRoles);
    notifyChange(newPrimaryRoles, newSecondaryRoles);
  };

  const handleSecondaryRoleChange = (primaryAppId, secondaryAppId, roleId) => {
    const newSecondaryRoles = {
      ...selectedSecondaryRoles,
      [primaryAppId]: {
        ...(selectedSecondaryRoles[primaryAppId] || {}),
      },
    };

    if (roleId) {
      newSecondaryRoles[primaryAppId][secondaryAppId] = roleId;
    } else {
      delete newSecondaryRoles[primaryAppId][secondaryAppId];
    }

    setSelectedSecondaryRoles(newSecondaryRoles);
    notifyChange(selectedPrimaryRoles, newSecondaryRoles);
  };

  const notifyChange = (primaryRoles, secondaryRoles) => {
    const allRoles = [];

    // Add primary roles with their child apps
    Object.entries(primaryRoles).forEach(([appId, roleId]) => {
      if (roleId) {
        // Find secondary roles for this primary app's children
        const childAppsRole = [];
        const secondaryRolesForThisApp = secondaryRoles[appId] || {};

        Object.entries(secondaryRolesForThisApp).forEach(
          ([childAppId, childRoleId]) => {
            if (childRoleId) {
              childAppsRole.push({
                app_id: childAppId,
                role: childRoleId,
              });
            }
          },
        );

        allRoles.push({
          app_id: appId,
          role: roleId,
          child_apps_role: childAppsRole,
        });
      }
    });

    onChange(allRoles);
  };

  if (isFetching) {
    return <Loader size="md" />;
  }

  const primaryApps = appSpaceRoles.filter(app =>
    app.flags?.includes('primary'),
  );
  // Systémové apky (admin, mcp) – top-level, ale bez 'primary' flagu.
  // Backend ich vracia len pre PPO, kde má organizácia rolu (reálne 'sportnet').
  const systemApps = appSpaceRoles.filter(
    app => app.flags?.includes('system') && !app.flags?.includes('primary'),
  );

  const renderApp = app => {
    const hasSelectedRole = selectedPrimaryRoles[app.app_id];
    const roles = app.roles || [];

    // Get child apps data - only secondary apps
    const childAppIds = app.childApps || [];
    const childAppsData = childAppIds
      .map(childAppId => appSpaceRoles.find(a => a.app_id === childAppId))
      .filter(childApp => childApp && childApp.flags?.includes('secondary'));

    return (
      <div key={app.app_id} style={{ marginBottom: '8px' }}>
        <div
          style={{
            marginBottom: '4px',
            fontSize: '13px',
            fontWeight: '500',
          }}
        >
          {app.name}
          {app.apiDocsUrl && (
            <a
              href={app.apiDocsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginLeft: '8px',
                fontSize: '12px',
                color: '#0066cc',
                fontWeight: 'normal',
              }}
              title={__('API dokumentácia')}
            >
              [docs]
            </a>
          )}
        </div>
        <select
          value={selectedPrimaryRoles[app.app_id] || ''}
          onChange={e => handlePrimaryRoleChange(app.app_id, e.target.value)}
          disabled={disabled}
          style={{ width: '100%', padding: '8px', borderRadius: '4px' }}
        >
          <option value="">{__('Žiadna')}</option>
          {roles.map(role => (
            <option key={role.role} value={role.role}>
              {role.name}
            </option>
          ))}
        </select>

        {/* Show child apps only if primary role is selected */}
        {hasSelectedRole && childAppsData.length > 0 && (
          <div style={{ marginLeft: '20px', marginTop: '8px' }}>
            {childAppsData.map(childApp => {
              const childRoles = childApp.roles || [];

              return (
                <div key={childApp.app_id} style={{ marginBottom: '8px' }}>
                  <div
                    style={{
                      marginBottom: '4px',
                      fontSize: '12px',
                      color: '#666',
                    }}
                  >
                    {childApp.name}
                    {childApp.apiDocsUrl && (
                      <a
                        href={childApp.apiDocsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          marginLeft: '6px',
                          fontSize: '11px',
                          color: '#0066cc',
                        }}
                        title={__('API dokumentácia')}
                      >
                        [docs]
                      </a>
                    )}
                  </div>
                  <select
                    value={
                      selectedSecondaryRoles[app.app_id]?.[childApp.app_id] ||
                      ''
                    }
                    onChange={e =>
                      handleSecondaryRoleChange(
                        app.app_id,
                        childApp.app_id,
                        e.target.value,
                      )
                    }
                    disabled={disabled}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                    }}
                  >
                    <option value="">{__('Žiadna')}</option>
                    {childRoles.map(role => (
                      <option key={role.role} value={role.role}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <Label required>{__('Role v aplikáciách')}</Label>
      <br />
      {primaryApps.map(renderApp)}
      {systemApps.length > 0 && (
        <>
          <div style={{ marginTop: '16px' }}>
            <Label>{__('Systémové aplikácie')}</Label>
          </div>
          <br />
          {systemApps.map(renderApp)}
        </>
      )}
    </div>
  );
};

export default RolesSelector;
